import { gql, useQuery } from '@apollo/client';
import { Date } from '@components/misc/date/date';
import { NoPermissionAlert } from '@components/misc/noPermissionAlert';
import type { BulkTradeRunAllocation } from '@interfaces/bulkTradeRunAllocation';
import {
  Chip,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { formatDecimalMoneyValue } from '@utils/common';
import { Box, CircularProgress, Grid, Typography } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const FETCH_BULK_TRADE_RUN_ALLOCATIONS = (
  disableCorpInventoryAccount: boolean,
) => gql`
  query fetchBulkTradeRunAllocations($input: FetchBulkTradeRunAllocationsInput!) {
    fetchBulkTradeRunAllocations(input: $input) {
      bulkTradeRunAllocations {
        id
        externalId
        account
        settlementCurrency
        tradeCurrency
        side
        symbol
        market
        fillPrice
        shareQuantity
        tradeDate
        settlementDate
        tradeValue
        ${!disableCorpInventoryAccount ? 'corporateOffsetAccount' : ''}
        fXRate
      }
      totalCount
    }
  }
`;

type BulkTradeRunAllocationsFilter = {
  bulkTradeRunId: string;
  isPTF?: boolean;
};

const BulkTradeRunAllocationsTable = ({
  filter,
}: {
  filter: BulkTradeRunAllocationsFilter;
}) => {
  const { t } = useTranslation(['components']);
  const canReadCustodianConnection = usePermission('read:custodian_connection');
  const canReadBulkTradeRun = usePermission('read:bulk_trade_run');
  const { custodianConnection } = useContext(UserContext);

  const hasCustodianConnectionPermission = canReadCustodianConnection;
  const disableCorpInventoryAccount = hasCustodianConnectionPermission
    ? !!custodianConnection?.disableCorpInventoryAccount
    : false;

  const [page, setPage] = useState(1);
  const pageSize = 15;

  const { loading, data, previousData } = useQuery(
    FETCH_BULK_TRADE_RUN_ALLOCATIONS(disableCorpInventoryAccount ?? false),
    {
      skip: !canReadBulkTradeRun,
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          filter: {
            ...filter,
          },
          pagination: {
            perPage: pageSize,
            offSet: (page - 1) * pageSize,
          },
        },
      },
    },
  );

  useEffect(
    () =>
      function cleanupOnUnmount() {
        setPage(1);
      },
    [],
  );

  useEffect(() => setPage(1), [filter]);

  if (!canReadBulkTradeRun)
    return <NoPermissionAlert missing='read:bulk_trade_run' />;

  return (
    <Box>
      {loading && !previousData ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : (
        <Grid container sx={{ overflow: 'hidden' }}>
          <Grid sx={{ overflow: 'auto' }} size={12}>
            <Table aria-label='table'>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.externalId')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.account')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.settlementCurrency')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.tradeCurrency')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.side')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.symbol')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.market')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.fillPrice')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.shareQuantity')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.tradeDate')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.settlementDate')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.tradeValue')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.corporateOffsetAccount')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='labelSmall'>
                      {t('bulkTradeRunAllocationsTable.fXRate')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(
                  data || previousData
                )?.fetchBulkTradeRunAllocations?.bulkTradeRunAllocations?.map(
                  (trade: BulkTradeRunAllocation) => (
                    <TableRow
                      hover
                      key={trade.id}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        textDecoration: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell scope='row'>{trade.externalId}</TableCell>
                      <TableCell scope='row'>{trade.account}</TableCell>
                      <TableCell scope='row'>
                        {trade.settlementCurrency}
                      </TableCell>
                      <TableCell scope='row'>{trade.tradeCurrency}</TableCell>
                      <TableCell>
                        <Chip size='small' label={trade.side} />
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {trade.symbol}
                      </TableCell>
                      <TableCell scope='row'>{trade.market}</TableCell>
                      <TableCell scope='row'>
                        {formatDecimalMoneyValue(trade.fillPrice, 6)}
                      </TableCell>
                      <TableCell>{trade.shareQuantity}</TableCell>
                      <TableCell>
                        <Date date={trade.tradeDate} />
                      </TableCell>
                      <TableCell>
                        <Date date={trade.settlementDate} />
                      </TableCell>
                      <TableCell>{trade.tradeValue}</TableCell>
                      <TableCell>
                        {disableCorpInventoryAccount
                          ? trade.corporateOffsetAccount
                          : ''}
                      </TableCell>
                      <TableCell>{trade.fXRate}</TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </Grid>
          <Pagination
            count={Math.ceil(
              ((data || previousData)?.fetchBulkTradeRunAllocations
                ?.totalCount ?? 0) / pageSize,
            )}
            page={page}
            onChange={(_e, newPage) => setPage(newPage)}
            sx={{
              p: 1,
              textAlign: 'right',
              '.MuiPagination-ul': {
                justifyContent: 'end',
              },
            }}
          />
        </Grid>
      )}
    </Box>
  );
};

export default BulkTradeRunAllocationsTable;
